'use client';

import { useBunja } from 'bunja/react';
import { PropsWithChildren, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { intervalSocketBunja } from '@flipster/r230522/original/interval/socket';
import { sendNativeRequest } from '@flipster/utils/simple-bridge/bridge';
import webviewHandler from '@flipster/utils/webview-handler';

import type { ChartTradeCallbacks } from './chart-trade-callbacks';
import { CustomHeader } from './custom-header';
import { Candles } from './custom-header/candles';
import { Indicators } from './custom-header/indicators';
import { WebviewIntervals } from './custom-header/intervals';
import { Plugins } from './custom-header/plugins';
import { HistoryPlugin } from './custom-header/plugins/history';
import { DraggablePositionsPlugin, DraggableTpSlPlugin } from './custom-header/plugins/order-trackers/draggable-orders';
import { LimitOrdersPlugin } from './custom-header/plugins/order-trackers/limit-orders';
import { ENABLED_FEATURE } from './custom-header/plugins/order-trackers/line-helpers/draggable-tpsl-line-helper/enabled-features';
import { SpotLimitOrdersPlugin } from './custom-header/plugins/order-trackers/spot-limit-orders';
import { SpotTriggerOrdersPlugin } from './custom-header/plugins/order-trackers/spot-trigger-orders';
import { TriggerOrdersPlugin } from './custom-header/plugins/order-trackers/trigger-orders';
import { PositionsPlugin } from './custom-header/plugins/positions';
import { TpSlPlugin } from './custom-header/plugins/tp-sl';
import { QuickStudies } from './custom-header/quick-studies';
import { getResolveSymbolInfoBaseFn } from './misc/data';
import { toWebviewChartSymbol } from './misc/helper';
import { widgetOptions } from './options';
import { datafeed } from './options/datafeed';
import { getDisabledFeatures, getEnabledFeatures } from './options/features';
import { SetupDrawing } from './plugins/setup-drawing';
import { TradingViewScope, intervalDataBunja, reconnectionLongTermBunja } from './socket/effect';
import TradingView from './trading-view';
import { TvLayout } from './tv-layout';

export interface TvChartWebviewFullchartProps extends ChartTradeCallbacks {
  symbol: string;
  osType: 'Android' | 'iOS' | 'unknown';
  version: string;
  isSpot?: boolean;
  isLoggedIn?: boolean;
  featureFlags?: Record<string, boolean>;
}

const TvChartWebviewFullchart = ({
  symbol,
  osType,
  version,
  isSpot,
  isLoggedIn,
  featureFlags,
  onOrderDragComplete,
  onOrderCancelClick,
  onPositionClose,
}: PropsWithChildren<TvChartWebviewFullchartProps>) => {
  const [tv, setTV] = useState<TradingView>();
  const [initialized, setInitialized] = useState(false);
  const indicatorCountRef = useRef(0);
  const isSpotLimitOrderEnabled = featureFlags?.['25q4-spot-limit-order'];
  // 게이팅: native가 URL hash의 enabled_features를 파싱해 featureFlags로 넘겨준다 (ERD-234).
  const isDraggableEnabled = featureFlags?.[ENABLED_FEATURE.DraggableTpSl] ?? false;
  // Spot: force :MID (native mid often has no suffix; sanitize must not map that to LAST).
  const chartSymbol = useMemo(() => toWebviewChartSymbol(symbol, isSpot), [symbol, isSpot]);
  // 앱(webview)은 드래그 scope out: 라인은 비편집으로 두고 ×(청산/삭제)만 브리지로 native에 위임한다.
  // onDragComplete는 타입상 필요하지만 비편집이라 호출되지 않는다(close-only).
  const draggableCallbacks = useMemo(
    () =>
      isDraggableEnabled && onOrderDragComplete && onOrderCancelClick
        ? { onDragComplete: onOrderDragComplete, onCancelClick: onOrderCancelClick }
        : undefined,
    [isDraggableEnabled, onOrderDragComplete, onOrderCancelClick],
  );

  useBunja(intervalDataBunja);
  useBunja(reconnectionLongTermBunja, [TradingViewScope.bind(tv)]);
  const { subscribeSocketIntervalData, unsubscribeSocketIntervalData, requestIntervalSnapshot } =
    useBunja(intervalSocketBunja);

  // TV Chart에게 symbol 정보를 가져오는 방법과 인터벌 데이터 구독/해지하는 방법 주입
  useLayoutEffect(() => {
    datafeed.isWebview = true;
    datafeed.resolveSymbolInfoBase = getResolveSymbolInfoBaseFn('hash');
    datafeed.requestIntervalSnapshot = requestIntervalSnapshot;
    datafeed.subscribeSocketIntervalData = subscribeSocketIntervalData;
    datafeed.unsubscribeSocketIntervalData = unsubscribeSocketIntervalData;
    const offSymbolNotResolved = datafeed.on('symbolNotResolved', () => {
      webviewHandler.postMessage({ action: 'info', params: { message: 'chart_need_to_reload' } });
    });

    return () => {
      offSymbolNotResolved();
    };
  }, [requestIntervalSnapshot, subscribeSocketIntervalData, unsubscribeSocketIntervalData]);

  useEffect(() => {
    if (tv) return;
    const instance = new TradingView(
      {
        symbol: chartSymbol,
        custom_css_url: './styles.css?v=5',
        enabled_features: getEnabledFeatures('webview-fullchart', osType, version, featureFlags),
        disabled_features: getDisabledFeatures('webview-fullchart', osType, version, featureFlags),
        datafeed,
        fullscreen: false,
        debug: false,
        ...widgetOptions,
      },
      { webview: true },
    );
    instance.on('initialize', () => setInitialized(true));
    setTV(instance);
  }, [chartSymbol, tv, version, osType, featureFlags]);

  useEffect(() => {
    tv?.setSymbol(chartSymbol);
  }, [chartSymbol, tv]);

  useEffect(() => {
    try {
      const chart = tv?.widget;

      const getIndicatorHeight = () =>
        tv?.activeChart
          ?.getAllPanesHeight()
          .slice(1)
          .reduce((acc, n) => acc + n, 0) || 0;

      const getIndicatorCount = () => tv?.activeChart?.getPanes().slice(1).length || 0;

      const subscribeSymbolChanged = () => {
        indicatorCountRef.current = 0;

        // symbol change시에 바로 height를 계산하면, 이전 symbol의 height를 계산하기 때문에 의도적으로 500ms 딜레이를 둠
        // symbol change되고, ui가 해당 symbol로 변경됐을 시점을 정확하게 트리거하기 어려워 setTimeout 사용
        setTimeout(() => {
          const height = getIndicatorHeight();
          sendNativeRequest({ name: 'updateIndicatorAreaHeight', callback: '', params: { height } });
        }, 500);
      };

      const handleStudyEvent = (_entityId: unknown, studyEventType: string) => {
        // 다음 이벤트 루프 때 실행하기 위해서 의도적으로 setTimeout을 설정함
        setTimeout(() => {
          if (studyEventType === 'create' || studyEventType === 'remove') {
            const newCount = getIndicatorCount();
            if (indicatorCountRef.current !== newCount) {
              const height = getIndicatorHeight();
              sendNativeRequest({ name: 'updateIndicatorAreaHeight', callback: '', params: { height } });
              indicatorCountRef.current = newCount;
            }
          }
        }, 0);
      };

      chart?.subscribe('study_event', handleStudyEvent);
      tv?.on('onSymbolChanged', subscribeSymbolChanged);

      return () => {
        chart?.unsubscribe('study_event', handleStudyEvent);
        tv?.off('onSymbolChanged', subscribeSymbolChanged);
      };
    } catch {
      return;
    }
  }, [tv]);

  return (
    <>
      {initialized && <SetupDrawing tv={tv} />}
      <TvLayout
        tv={tv}
        customHeader={
          initialized && (
            <CustomHeader.HeaderSection>
              <WebviewIntervals />
              <Candles />
              <Indicators />
              <QuickStudies />
              <Plugins>
                {!isSpot &&
                  isLoggedIn &&
                  (isDraggableEnabled && onPositionClose ? (
                    <DraggablePositionsPlugin onPositionClose={onPositionClose} />
                  ) : (
                    <PositionsPlugin />
                  ))}
                {!isSpot &&
                  isLoggedIn &&
                  (draggableCallbacks ? (
                    <DraggableTpSlPlugin callbacks={draggableCallbacks} isWebview />
                  ) : (
                    <TpSlPlugin variant='short' />
                  ))}
                {!isSpot && isLoggedIn && <LimitOrdersPlugin />}
                {!isSpot && isLoggedIn && <TriggerOrdersPlugin />}
                {isSpotLimitOrderEnabled && isSpot && isLoggedIn && <SpotLimitOrdersPlugin />}
                {isSpot && isLoggedIn && <SpotTriggerOrdersPlugin />}
                {isLoggedIn && <HistoryPlugin />}
              </Plugins>
            </CustomHeader.HeaderSection>
          )
        }
        fullscreen
      />
    </>
  );
};

export default TvChartWebviewFullchart;
